/**
 * Sign-in.
 *
 * The box takes a phone number, an email address **or** a member code. A phone
 * number stopped being unique when [D-02] was reversed — an Indian gym lists a
 * husband and wife on one mobile — so the server may answer 409
 * `ambiguous_identifier` with the accounts that number reaches. That is not an
 * error to show and forget: it is a question, and this page asks it, then re-posts
 * the same password with the chosen `user_id`.
 */
import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";

import { AuthShell } from "@/app/auth/AuthShell";
import { FormRootError, TextField } from "@/components/app/form-field";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { applyServerErrors, fetchCsrfToken, toApiError } from "@/lib/api";
import { AmbiguousIdentifierError, useLogin, useSession, type LoginChoice } from "@/lib/auth";
import type { SessionResponse } from "@/types/api";

const loginSchema = z.object({
  // Deliberately not an E.164 regex any more: "priya@example.com" and
  // "MEM-000123" are both valid here, and the server is the only thing that can
  // say which account a string means.
  phone: z.string().trim().min(1, "auth.identifierRequired"),
  password: z.string().min(1, "auth.passwordRequired"),
});

type LoginValues = z.infer<typeof loginSchema>;

/** Where a user lands after signing in, from the `user_type` UI hint. */
function landingPath(session: SessionResponse): string {
  if (session.user.has_staff_profile || session.permissions.length > 0) return "/";
  if (session.user.has_trainer_profile) return "/coach";
  if (session.user.has_member_profile) return "/portal";
  return "/";
}

export default function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useSession();
  const login = useLogin();
  const [choices, setChoices] = useState<LoginChoice[] | null>(null);

  const from = (location.state as { from?: string } | null)?.from;

  // Prime the CSRF cookie before the first POST — the login itself is a mutation.
  useEffect(() => {
    void fetchCsrfToken();
  }, []);

  useEffect(() => {
    if (searchParams.get("error") === "impersonation_failed") {
      toast.error(t("auth.impersonationFailed"));
    }
  }, [searchParams, t]);

  useEffect(() => {
    if (isAuthenticated) navigate(from ?? "/", { replace: true });
  }, [isAuthenticated, from, navigate]);

  const form = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { phone: "", password: "" },
  });

  const attempt = async (values: LoginValues, userId?: number) => {
    try {
      const session = await login.mutateAsync({ ...values, user_id: userId });
      navigate(from ?? landingPath(session), { replace: true });
    } catch (error) {
      if (error instanceof AmbiguousIdentifierError) {
        setChoices(error.choices);
        return;
      }
      setChoices(null);
      const apiError = toApiError(error);
      if (apiError.code === "invalid_credentials") {
        form.setError("root", { message: t("auth.invalidCredentials") });
        return;
      }
      if (apiError.status === 403) {
        form.setError("root", { message: t("auth.inactiveAccount") });
        return;
      }
      if (!applyServerErrors(form, apiError)) {
        form.setError("root", { message: apiError.detail || t("error.unexpected") });
      }
    }
  };

  const onSubmit = (values: LoginValues) => attempt(values);

  const onChoose = (choice: LoginChoice) => attempt(form.getValues(), choice.user_id);

  return (
    <AuthShell
      title={t("auth.loginTitle")}
      description={choices ? t("auth.ambiguousBody") : t("auth.loginSubtitle")}
      footer={
        <Link
          to="/forgot-password"
          className="text-muted-foreground hover:text-foreground underline underline-offset-4"
        >
          {t("auth.forgotLink")}
        </Link>
      }
    >
      {choices ? (
        <div className="space-y-3">
          <h2 className="text-base font-medium">{t("auth.ambiguousTitle")}</h2>
          <ul className="space-y-2">
            {choices.map((choice) => (
              <li key={choice.user_id}>
                <Button
                  type="button"
                  variant="outline"
                  className="h-auto w-full justify-start py-3 text-left"
                  disabled={login.isPending}
                  onClick={() => void onChoose(choice)}
                >
                  <span className="flex flex-col">
                    <span className="font-medium">{choice.full_name}</span>
                    {choice.member_code ? (
                      <span className="text-muted-foreground text-xs">{choice.member_code}</span>
                    ) : null}
                  </span>
                </Button>
              </li>
            ))}
          </ul>
          <Button
            type="button"
            variant="ghost"
            className="w-full"
            onClick={() => {
              setChoices(null);
              form.reset({ phone: "", password: "" });
            }}
          >
            {t("auth.ambiguousBack")}
          </Button>
        </div>
      ) : (
        <Form {...form}>
          <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)} noValidate>
            <FormRootError message={form.formState.errors.root?.message} />

            <TextField
              control={form.control}
              name="phone"
              label={t("auth.identifierLabel")}
              description={t("auth.identifierHint")}
              placeholder={t("auth.identifierPlaceholder")}
              autoComplete="username"
              required
            />

            <TextField
              control={form.control}
              name="password"
              type="password"
              autoComplete="current-password"
              label={t("auth.passwordLabel")}
              placeholder={t("auth.passwordPlaceholder")}
              required
            />

            <Button type="submit" className="w-full" disabled={login.isPending}>
              {login.isPending ? t("auth.signingIn") : t("auth.signIn")}
            </Button>
          </form>
        </Form>
      )}
    </AuthShell>
  );
}
